import { Tooltip } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/styles";
import * as React from "react";

import { PageActionButton, RelodeTheme, Text } from "../";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    ExclamationIcon,
} from "../../assets/icons";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            ...{
                navText: {
                    marginRight: theme.spacing(1),
                    whiteSpace: "nowrap",
                },
                tooManyResultsIcon: {
                    marginRight: theme.spacing(1),
                },
                navigation: {
                    display: "flex",
                    alignItems: "center",
                },
            },
            ...theme.relode.controls.paginator,
        }),
    { name: "Paginator" }
);

type PaginatorCSSProperties = "navText" | "tooManyResultsIcon" | "navigation";
export type PaginatorStyles = Partial<
    Record<
        PaginatorCSSProperties,
        CSSProperties | ((props?: {}) => CSSProperties)
    >
>;

interface Props {
    baseId?: string;
    range?: { start: number; end: number };
    current?: number;
    total?: number;
    showAsRange?: boolean;
    onNavigate: (direction: "next" | "prev") => void;
    isBusy?: boolean;
    entityName?: string;
    enableTooManyResultsMessage?: boolean;
    disabled?: boolean;
}

export const Paginator = (props: Props) => {
    const {
        baseId,
        onNavigate,
        total = 0,
        showAsRange = false,
        current = 0,
        range = { start: 0, end: 0 },
        isBusy,
        entityName,
        enableTooManyResultsMessage = false,
        disabled = false,
    } = props;

    const classes = useStyles({});
    const extendedId = `${baseId}Paginator`;

    const navText = showAsRange
        ? `${range.start} - ${range.end} of ${total}`
        : `${current} of ${total}`;

    const prevDisabled =
        (isBusy ? isBusy : showAsRange ? range.start === 1 : current === 1) ||
        disabled;

    const nextDisabled =
        (isBusy
            ? isBusy
            : showAsRange
            ? range.end === total
            : current === total) || disabled;

    return (
        <div className={classes.navigation} id={`${extendedId}Wrapper`}>
            {enableTooManyResultsMessage === true && total >= 1000 && (
                <Tooltip
                    placement="left"
                    title="Your search returned more than 1000 results. Only the first 1000 are shown.  Try providing additional search criteria to reduce your results."
                >
                    <div>
                        <ExclamationIcon
                            color="error"
                            className={classes.tooManyResultsIcon}
                        />
                    </div>
                </Tooltip>
            )}
            <Text
                className={classes.navText}
                variant="body1"
                color="primary"
                id={`${extendedId}TotalText`}
                dataCy={`${baseId}-paginator-text`}
            >
                {navText}
            </Text>
            <PageActionButton
                data-cy={`${baseId}-paginator-previous-button`}
                id={`${extendedId}PrevButton`}
                title={`previous ${entityName || ""}`}
                color="primary"
                disabled={prevDisabled}
                onClick={() => onNavigate("prev")}
            >
                <ChevronLeftIcon />
            </PageActionButton>
            <PageActionButton
                data-cy={`${baseId}-paginator-next-button`}
                id={`${extendedId}NextButton`}
                title={`next ${entityName || ""}`}
                color="primary"
                disabled={nextDisabled}
                onClick={() => onNavigate("next")}
            >
                <ChevronRightIcon />
            </PageActionButton>
        </div>
    );
};
